package dev.lingvo.shared

import kotlinx.serialization.Serializable

@Serializable
enum class StoreConnectionStatus { UNVERIFIED, VERIFIED, REJECTED, REVOKED }

/**
 * A new credential for a store account.
 *
 * Exactly one of [appStore] and [googlePlay] is read, chosen by [platform]. The secret fields are
 * accepted here once and never appear in any response: connections come back as
 * [StoreConnectionDto], which carries only the last four characters.
 */
@Serializable
data class CreateStoreConnectionRequest(
    val platform: StorePlatform,
    val label: String,
    val appStore: AppStoreKeyInput? = null,
    val googlePlay: GooglePlayKeyInput? = null,
)

@Serializable
data class AppStoreKeyInput(
    /** Empty for an individual key, which carries one person's role and sees only their apps. */
    val issuerId: String = "",
    val keyId: String,
    /** The text of the `.p8` file, including the BEGIN/END lines. */
    val privateKey: String,
)

@Serializable
data class GooglePlayKeyInput(
    /** The service-account JSON key file, verbatim. */
    val serviceAccountJson: String,
)

@Serializable
data class StoreConnectionDto(
    val id: String,
    val platform: StorePlatform,
    val label: String,
    /** The last four characters of the secret; nothing else of it is ever returned. */
    val credentialHint: String,
    /** Non-secret identifiers: Apple issuer and key id, or the Play service-account email. */
    val identity: Map<String, String> = emptyMap(),
    val status: StoreConnectionStatus,
    val lastErrorCode: String? = null,
    val createdAt: String,
    val lastVerifiedAt: String? = null,
    /** Apple: explicitly selected apps only. An empty list allows no app. */
    val allowedAppRefs: List<String> = emptyList(),
)

@Serializable
data class BindStoreAppRequest(
    val connectionId: String,
    /** Apple's numeric app id, or the Play package name. */
    val storeAppRef: String,
    val displayName: String? = null,
    /** Project locale to store locale, for the cases the default match gets wrong. */
    val localeMap: Map<String, String> = emptyMap(),
)

@Serializable
data class StoreAppDto(
    val id: String,
    val platform: StorePlatform,
    val connectionId: String,
    val storeAppRef: String,
    val displayName: String? = null,
    val localeMap: Map<String, String> = emptyMap(),
    val lastImportedAt: String? = null,
)

@Serializable
data class StoreImportResultDto(
    val importedLocales: List<String>,
    val fieldCount: Int,
    /** Store locales with no matching project locale, left out rather than silently added. */
    val skippedLocales: List<String> = emptyList(),
    val warnings: List<String> = emptyList(),
)

@Serializable
data class FastlaneImportRequest(
    /** Relative path to UTF-8 contents, for the files under a fastlane `metadata` folder. */
    val files: Map<String, String>,
)

@Serializable
enum class StoreFieldStatus {
    UNCHANGED,
    CHANGED,

    /** Differs from the store but is not approved, so it is not pushed. */
    NOT_REVIEWED,

    /** Over the store's limit; refused. */
    OVER_LIMIT,
}

@Serializable
data class StoreFieldDiffDto(
    val field: StoreField,
    val remote: String? = null,
    val local: String? = null,
    val status: StoreFieldStatus,
    val used: Int = 0,
    val limit: Int = 0,
    val unit: StoreLimitUnit = StoreLimitUnit.CHARACTERS,
)

@Serializable
data class StoreLocaleDiffDto(
    val storeLocale: String,
    val projectLocale: String,
    val fields: List<StoreFieldDiffDto>,
    val issues: List<String> = emptyList(),
    /** True when at least one field would be written and none is blocked. */
    val pushable: Boolean,
)

@Serializable
data class StoreDiffDto(
    val platform: StorePlatform,
    val locales: List<StoreLocaleDiffDto>,
    /** False when the store only accepts some fields right now; see [stateNote]. */
    val editable: Boolean,
    val stateNote: String? = null,
    /**
     * Fingerprint of exactly what this diff shows. A push must present it, so the person who
     * reviewed a diff is the person whose reviewed text goes live, even if someone edits in between.
     */
    val diffToken: String,
)

@Serializable
data class StorePushRequest(
    val diffToken: String,
    /** Store locales to push; null means every pushable locale in the diff. */
    val locales: List<String>? = null,
)

@Serializable
enum class StorePushStatus { PENDING, SUCCEEDED, PARTIAL, QUEUED, FAILED }

@Serializable
data class StorePushDto(
    val id: String,
    val kind: String,
    val status: StorePushStatus,
    val actorLabel: String,
    val pushedLocales: List<String> = emptyList(),
    val failedLocales: Map<String, String> = emptyMap(),
    val queuedReason: String? = null,
    val errorCode: String? = null,
    val canRollback: Boolean = false,
    val createdAt: String,
    val finishedAt: String? = null,
)

@Serializable
data class FastlaneExportDto(
    /** Relative path to file contents, in the layout `fastlane deliver` and `supply` read. */
    val files: Map<String, String>,
    /** Fields left out because they are not approved yet. Exporting never ships unreviewed text. */
    val skippedUnreviewed: Int = 0,
)

@Serializable
data class FitKeywordsRequest(
    /** The project locale whose keyword draft is fitted. */
    val locale: String,
)

/**
 * What fitting a keyword draft did. The fitted list is saved as an unapproved draft: keywords are
 * review-only, so a person still approves it before it can be pushed.
 */
@Serializable
data class StoreKeywordPlanDto(
    val locale: String,
    val value: String,
    val bytes: Int,
    val kept: List<String> = emptyList(),
    /** Words the name or subtitle already index, which Apple counts anyway. */
    val droppedIndexed: List<String> = emptyList(),
    val droppedDuplicates: List<String> = emptyList(),
    /** Words that no longer fit the 100-byte budget, least important last. */
    val droppedOverBudget: List<String> = emptyList(),
    /** False when the draft already fit and nothing was written. */
    val changed: Boolean,
)
